import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface GrafanaGraphiteFormProps {
  serverErrors?: ServerErrors;
}

export const GrafanaGraphiteForm = ({serverErrors}: GrafanaGraphiteFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.graphite_url"
            label="Graphite Endpoint URL"
            required={true}
            tooltip="Graphite-compatible metrics endpoint, e.g. https://graphite-prod-XX-prod-XX.grafana.net/graphite/metrics."
            autoFocus={true}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.metrics_instance_id"
            label="Metrics Instance ID"
            required={true}
            tooltip="Numeric instance ID for Graphite basic auth."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.metrics_api_token"
            label="Metrics API Token"
            required={true}
            tooltip="API key or service account token for Graphite metrics ingestion (basic auth)."
          />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12 mb-3">
          <TextAreaField
            name="settings.tags"
            label="Tags"
            tooltip="Optional Graphite tags attached to every pushed metric. One key=value pair per line, or comma-separated."
            placeholder="source=uptime_com&#10;env=prod"
            rows={3}
          />
        </div>
      </div>

      <SetupInfo>
        <div className="p-2">
          Push Uptime.com response time metrics to Grafana Cloud's Graphite-compatible ingestion endpoint.
        </div>
        <div className="p-2">
          <span className="pulseTextBodyBold">Graphite Endpoint URL</span>
          <br />
          Find your Graphite metrics endpoint in your Grafana Cloud portal under the Graphite data source details. It
          typically looks like <code>https://graphite-prod-XX-prod-XX.grafana.net/graphite/metrics</code>.
        </div>
        <div className="p-2">
          <span className="pulseTextBodyBold">Metrics Instance ID</span>
          <br />
          Your Grafana Cloud metrics instance ID (a numeric value), found in the Grafana Cloud portal under the Graphite
          data source details.
        </div>
        <div className="p-2">
          <span className="pulseTextBodyBold">Metrics API Token</span>
          <br />A Grafana Cloud API key or service account token used as the basic auth password for Graphite metrics
          ingestion.
        </div>
        <div className="p-2">
          <span className="pulseTextBodyBold">Tags</span>
          <br />
          Optional Graphite tags attached to every pushed metric. Enter one <code>key=value</code> pair per line, or
          separate multiple pairs with commas (e.g. <code>source=uptime_com</code>, <code>env=prod</code>).
        </div>
        {IntegrationModuleInfo[IntegrationModules.GRAFANA_GRAPHITE].url && (
          <div className="p-2">
            <a target="_blank" href={IntegrationModuleInfo[IntegrationModules.GRAFANA_GRAPHITE].url}>
              Visit Grafana &rarr;
            </a>
          </div>
        )}
      </SetupInfo>
    </>
  );
};
